<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">
      {{ bt("pageTitle") }}
    </app-top-bar>

    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <template v-if="!showComplete">
        <v-container v-if="step === 'serial'">
          <h1 class="page-title mb-6 text-on-surface-variant">
            {{ bt("mainTitle") }}
          </h1>
          <p class="mb-6 text-on-surface-variant">
            {{ bt("description") }}
          </p>

          <v-text-field
            ref="fieldRef"
            :model-value="serialValue"
            @update:model-value="onSerialInput"
            :label="bt('serialNumberLabel')"
            :hint="bt('hint')"
            @keydown="filterKeys"
            variant="underlined"
            color="primary"
            hide-details="auto"
            background-color="surface"
            density="comfortable"
            class="text-on-surface-variant mb-2"
          />

          <p
            v-if="patternDisplay"
            class="text-caption text-medium-emphasis mb-4"
          >
            {{ bt("patternLabel") }}: {{ patternDisplay }}
          </p>

        <v-img
          class="mx-auto flow-image"
          :class="{ 'custom-flow-image': isCustomImage }"
          contain
          :src="flowImageSource"
        />
        </v-container>
      </template>

      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="!showComplete && step === 'serial'">
      <continue-footer-button
        :disabled="!isValid"
        :loading="isAdvancing"
        @click="advance"
      />
    </app-footer>
    <app-footer v-if="showComplete">
      <continue-footer-button :loading="isAdvancing" @click="advanceFlow" />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { usePageBackground } from "@/composables/usePageBackground";
import { useBrandText } from "@/composables/useBrandText";
import { useStepFlow } from "@/composables/useStepFlow";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";
import { showToast } from "@/utils/toast";
import { applyMask, describePattern, extractLiteralPrefix, validateSerial } from "@/utils/serialMask";
import { useI18n } from "vue-i18n";
import defaultSerialImage from "@/assets/images/serial.png";

const { t } = useI18n();
const app = useAppStore();
const api = useApiStore();
const { mainStyle, hasBackgroundImage } = usePageBackground();
const bt = useBrandText("serialNumber", "bikeSerialNumber", t);

const step = ref<"serial" | "done" | "">("");
const fieldRef = ref<{ $el: HTMLElement } | null>(null);
const serialValue = ref(api.bikeSerialNumber || "");
const pathPage = '/bike-serial-number';

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);

const flowImageSource = computed(() => {
  return app.parameters?.flowAssets?.serial_number || defaultSerialImage;
});

const isCustomImage = computed(() => {
  return !!app.parameters?.flowAssets?.serial_number;
});

const strictPattern = computed(() => {
  const rules = api.serialRules;
  return rules?.pattern && rules.strict ? rules.pattern : null;
});

const patternDisplay = computed(() => describePattern(api.serialRules?.pattern ?? null));

function normalize(input: string): string {
  if (strictPattern.value) {
    return applyMask(input, strictPattern.value);
  }
  return input ? String(input).toUpperCase().replace(/\W/g, "") : "";
}

function onSerialInput(input: string) {
  const next = normalize(input ?? "");
  serialValue.value = next;
  nextTick(() => {
    const nativeInput = fieldRef.value?.$el?.querySelector("input");
    if (nativeInput && nativeInput.value !== serialValue.value) {
      nativeInput.value = serialValue.value;
    }
  });
}

const isValid = computed(() =>
  validateSerial(serialValue.value, api.serialRules)
);

const isComplete = computed(() => api.isItemComplete("serial"));

function filterKeys(event: KeyboardEvent) {
  if (/^\W$/.test(event.key)) event.preventDefault();
}

async function advance() {
  api.bikeSerialNumber = serialValue.value;
  const hasTag = await api.checkIfBikeHasTag(serialValue.value);
  if (hasTag) {
    showToast.warning({
      title: bt("dialogTitle"),
      description: bt("warningFoundTag"),
    });
    return;
  }
  try {
    await advanceFlow(async () => {
      await api.sendBikeSerialNumber(serialValue.value);
    });
  } catch (error) {
    console.error(error);
  }
}

onMounted(async () => {
  step.value = "serial";
  const rules = await api.loadSerialRules().catch(() => null);
  if (rules?.pattern && !serialValue.value) {
    serialValue.value = rules.strict
      ? applyMask("", rules.pattern)
      : extractLiteralPrefix(rules.pattern);
  }
});
</script>

<style scoped>
.flow-image {
  max-height: 40vh;
}

.custom-flow-image {
  border-radius: 8px;
  padding: 32px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 24px;
  margin-bottom: 24px;
  overflow: hidden;
}

.custom-flow-image :deep(.v-img__img) {
  transform: scale(1.25);
}
</style>
